import {
  IGetAIReviewJobDetailsResponse,
  JobType,
  ProtocolNameItem,
} from '../../../../../api/types/aiReview.type';
import {
  CaseContext,
  emptyCaseContext,
  emptyIssueCoding,
  emptyRelevanceCriteria,
  IssueCoding,
  JobDetailFormData,
  ProtocolFormData,
  RelevanceCriteria,
  SourceSelectionMode,
} from '../../../../../redux/slice/aiReviewSliceV2';
import {
  createEmptyCategory,
  createEmptyIssueCode,
} from '../../common/issue-codes-tab/utils';

export type MappedEditJobState = {
  jobType: JobType;
  formData: JobDetailFormData;
  protocol: ProtocolFormData;
};

/** AVR-23583: names is active-only. Null/0 means no review-protocol association. */
export const isReviewProtocolInactive = (
  reviewProtocolID?: number | null,
  protocolNames: ProtocolNameItem[] = []
): boolean => {
  if (reviewProtocolID == null || reviewProtocolID === 0) {
    return false;
  }

  return !protocolNames.some((item) => item.protocolId === reviewProtocolID);
};

/** Modified jobs display the original protocol; check that ID, not the saved-as ID. */
export const getProtocolIdForInactivityCheck = (
  isProtocolModified: boolean,
  reviewProtocolID?: number | null,
  parentProtocolID?: number | null
): number | null | undefined =>
  isProtocolModified ? parentProtocolID : reviewProtocolID;

const SOURCE_MODES: SourceSelectionMode[] = ['SAVEDSEARCH', 'TAG', 'FOLDER'];

const toOptionalId = (value?: number | null): number | null =>
  value == null || value === 0 ? null : value;

const toSourceSelectionMode = (sourceType?: string): SourceSelectionMode => {
  const normalized = (sourceType ?? 'SAVEDSEARCH').toUpperCase();
  return SOURCE_MODES.includes(normalized as SourceSelectionMode)
    ? (normalized as SourceSelectionMode)
    : 'SAVEDSEARCH';
};

const splitProtocolVersionName = (
  value: string
): {protocolName: string; protocolVersionName?: string} => {
  const match = value.trim().match(/^(?<protocolName>.+?)\s+(?<version>v\d+)$/i);

  if (!match?.groups) {
    return {protocolName: value.trim()};
  }

  return {
    protocolName: match.groups.protocolName,
    protocolVersionName: match.groups.version,
  };
};

const mergeCaseContext = (value: unknown): CaseContext => {
  const empty = emptyCaseContext();
  if (value == null) {
    return empty;
  }
  if (typeof value === 'string') {
    return {...empty, executive_summary: value};
  }
  if (typeof value === 'object' && !Array.isArray(value)) {
    const merged = {...empty, ...(value as Partial<CaseContext>)};
    return (Object.keys(empty) as Array<keyof CaseContext>).reduce(
      (acc, key) => ({
        ...acc,
        [key]: acc[key] == null ? empty[key] : acc[key],
      }),
      merged
    );
  }
  return empty;
};

const mergeIssueCoding = (value: unknown): IssueCoding => {
  const empty = emptyIssueCoding();
  if (value == null || Array.isArray(value)) {
    return empty;
  }
  if (typeof value === 'object') {
    const merged = {...empty, ...(value as Partial<IssueCoding>)};

    return {
      ...merged,
      top_level_issue_categories: (
        merged.top_level_issue_categories ?? empty.top_level_issue_categories
      ).map((category) => ({...createEmptyCategory(), ...category})),
      issue_codes: (merged.issue_codes ?? empty.issue_codes).map((code) => {
        const emptyCode = createEmptyIssueCode();
        const mergedCode = {...emptyCode, ...code};
        return {
          ...mergedCode,
          include_if_any_apply:
            mergedCode.include_if_any_apply ?? emptyCode.include_if_any_apply,
          exclude_if_any_apply:
            mergedCode.exclude_if_any_apply ?? emptyCode.exclude_if_any_apply,
          responsive_examples:
            mergedCode.responsive_examples ?? emptyCode.responsive_examples,
          non_responsive_examples:
            mergedCode.non_responsive_examples ??
            emptyCode.non_responsive_examples,
        };
      }),
      cross_issue_overlap_rules:
        merged.cross_issue_overlap_rules ?? empty.cross_issue_overlap_rules,
      edge_case_handling: merged.edge_case_handling ?? empty.edge_case_handling,
    };
  }
  return empty;
};

const mergeRelevanceCriteria = (value: unknown): RelevanceCriteria => {
  const empty = emptyRelevanceCriteria();
  if (value == null) {
    return empty;
  }
  if (typeof value === 'string') {
    return {...empty, master_relevance_standard: value};
  }
  if (typeof value === 'object' && !Array.isArray(value)) {
    return {...empty, ...(value as Partial<RelevanceCriteria>)};
  }
  return empty;
};

export const mapJobDetailsToEditState = (
  data: IGetAIReviewJobDetailsResponse,
  jobId?: number
): MappedEditJobState => {
  const caseContext = mergeCaseContext(data.caseContext);
  const issueCoding = mergeIssueCoding(data.issueCodes);
  const relevanceCriteria = mergeRelevanceCriteria(data.relevanceCriteria);
  const isManual = Boolean(data.isManual);
  const reviewProtocolVersionID = toOptionalId(data.reviewProtocolVersionID);
  const parentProtocolVersionID = toOptionalId(data.parentProtocolVersionID);
  const reviewProtocolID = toOptionalId(data.reviewProtocolID);
  const parentProtocolID = toOptionalId(data.parentProtocolID);
  const resolvedVersionId =
    reviewProtocolVersionID ?? parentProtocolVersionID ?? undefined;
  const isProtocolModified = Boolean(data.isProtocolModified);
  // Modified jobs keep the original selected name on parentProtocolVersion;
  // reviewProtocolVersion may be empty or the post-save "Saved As" name.
  const protocolDisplayName = isProtocolModified
    ? data.parentProtocolVersion?.trim() || data.protocolName?.trim() || ''
    : data.reviewProtocolVersion?.trim() || data.protocolName?.trim() || '';
  const {protocolName, protocolVersionName} =
    splitProtocolVersionName(protocolDisplayName);

  const protocol: ProtocolFormData = {
    protocolId: isManual ? undefined : (reviewProtocolID ?? undefined),
    protocolVersionId: isManual ? undefined : resolvedVersionId,
    protocolName: isManual ? '' : protocolName,
    protocolVersionName: isManual ? undefined : protocolVersionName,
    caseContext,
    relevanceCriteria,
    issueCoding,
    status: 'draft',
  };

  const groupID = toOptionalId(data.groupID);
  const formData: JobDetailFormData = {
    jobId: jobId ?? Number(data.jobId),
    jobDetail: {
      jobName: data.jobName ?? '',
      description: data.description ?? '',
      selectedGroup:
        groupID != null ? {id: groupID, name: data.groupName ?? ''} : null,
    },
    caseContext,
    relevanceCriteria,
    issueCoding,
    sourceSelection: data.sourceID ?? 0,
    sourceSelectionMode: toSourceSelectionMode(data.sourceType),
    sourceSelectionName: data.sourceName ?? '',
    totalDocs: null,
    groundTruthTagId: data.groundTruthTagID ?? data.groundTruthTagId ?? null,
    reviewProtocolVersionID: isManual ? null : reviewProtocolVersionID,
    parentProtocolVersionID: isManual ? null : parentProtocolVersionID,
    parentProtocolID: isManual ? null : parentProtocolID,
    isProtocolModified,
    parentReviewJobID: toOptionalId(data.parentReviewJobID) ?? undefined,
    isManual,
  };

  return {
    jobType: data.jobType === 'ISSUECODE' ? 'ISSUECODE' : 'RELEVANCY',
    formData,
    protocol,
  };
};

export const mapJobDetailsToCloneState = (
  data: IGetAIReviewJobDetailsResponse,
  sourceJobId: number
): MappedEditJobState => {
  const mapped = mapJobDetailsToEditState(data);
  const {jobId: _jobId, ...formWithoutJobId} = mapped.formData;

  return {
    jobType: mapped.jobType,
    protocol: mapped.protocol,
    formData: {
      ...formWithoutJobId,
      jobDetail: {
        ...mapped.formData.jobDetail,
        jobName: '',
      },
      sourceSelection: 0,
      sourceSelectionMode: 'SAVEDSEARCH',
      sourceSelectionName: '',
      totalDocs: null,
      parentReviewJobID: sourceJobId,
    },
  };
};
